import React from "react";
import { observer } from "mobx-react-lite";
import styles from "./CompositionStatus.module.scss";
import {
  SuccessCheck,
  FailCheck,
  StaticLoadingIcon,
  OpenLinkIcon,
  CloseIcon,
  RedoIcon,
} from "../../baseComponents/Icons";
import { theme } from "../../../theme";
import { FilteredComposition } from "./CompositionList";
import { dateFormatter } from "../../../store/utils/utilMethods";
import { postToServer } from "../../../store/utils/postToServer";
import { LoadingBar } from "../LoadingBar/Loadingbar";
import { env } from "../../../environments";
import { useMst } from "../../../store/root";
import { testBeforeRender } from "../../../jsxProviders";
import { Scene } from "../../../store/types";
import { getUpdatedAt } from "../../../store/utils/sceneUpdateAt";
import { environment } from "../../../store/utils/consts";
const { primary, background2, teal_80 } = theme;

type Props = {
  composition: FilteredComposition;
  status: string;
  hover: boolean;
  currentScene: Scene;
};

export const CompositionStatus = observer(
  ({ status, hover, composition, currentScene }: Props) => {
    const {
      projectStore: { selectedSceneExists, selectedProjectId },
      compositionStore: {
        removeRenderedComposition,
        setRenderedCompositions,
        selectedCompositionsId,
        setQueuedCompositions,
      },
      renderStore: {
        isRendering,
        setProcessIsCancelled,
        setIgnoreProblemLayers,
      },
      uiStore: { filterComposition, resetProblemLayersData },
    } = useMst();

    const isSelected = (composition: FilteredComposition) =>
      selectedCompositionsId.some((comp) => comp === composition.id);
    /**
     * Method contains the logic to analyze the return from sceneProperties and return the correct jsx
     * @param name
     * @returns jsx to render
     */
    const renderUpdatedAt = (name: string) => {
      const updatedAt = getUpdatedAt(currentScene);
      switch (updatedAt) {
        case undefined:
          return <></>;
        case null:
          return <p className={styles.UpdatedAt}>Rendered</p>;
        default:
          let date;
          try {
            date = dateFormatter(new Date(updatedAt));
          } catch (e) {
            date = "";
          }
          return (
            <p
              className={[
                styles.UpdatedAt,
                (filterComposition || isRendering) && !isSelected(composition)
                  ? styles.Disabled
                  : "",
              ].join(" ")}
            >
              {date}
            </p>
          );
      }
    };
    const openLinkIcon = (
      <div
        title="Open Studio"
        style={{ display: "inherit" }}
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();

          postToServer("openUrl", {
            url: `${env[environment].studio}${selectedProjectId}/layers/${composition.name}`,
          });
        }}
      >
        <OpenLinkIcon view="render" />
      </div>
    );

    const hoverOnSelected = (
      <div className={styles.HoverIcons}>
        <div
          title="Re-render Composition"
          style={{ display: "inherit" }}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            console.log("click");
            setQueuedCompositions(composition.id);
            setProcessIsCancelled(false); // Reset before render
            removeRenderedComposition(composition.id);
            resetProblemLayersData();
            setIgnoreProblemLayers(false);
            !isRendering && testBeforeRender();
          }}
        >
          <RedoIcon view="render" />
        </div>{" "}
        {openLinkIcon}
      </div>
    );

    let compositionStatus;
    switch (status) {
      case "success":
        compositionStatus = hover ? hoverOnSelected : <SuccessCheck />;
        break;
      case "fail":
        compositionStatus = hover ? hoverOnSelected : <FailCheck />;
        break;
      case "loading":
        compositionStatus = <LoadingBar id={composition.id} />;
        break;
      case "waiting":
        compositionStatus = (
          <div onClick={() => setRenderedCompositions(composition.id, false)}>
            <CloseIcon />
          </div>
        );
        break;
      case "notRendering":
        compositionStatus =
          hover && selectedSceneExists(composition.name) ? (
            isSelected(composition) ? (
              hoverOnSelected
            ) : (
              openLinkIcon
            )
          ) : (
            <div className={styles.Date}>
              {renderUpdatedAt(composition.name)}
            </div>
          );
        break;
      default:
        compositionStatus = <div></div>;
        break;
    }
    return compositionStatus;
  }
);
export const checkBox = (backgroundColor: string) => (
  <div
    style={{
      borderRadius: "3.5px",
      minWidth: "12px",
      height: "12px",
      alignSelf: "center",
      backgroundColor,
    }}
  ></div>
);

/**
 * Jsx for the left icon of each composition
 * State 1: Selected
 * State 2: Unselected
 * State 3: Disabled
 * State 3: Rendering
 * state 4: Waiting to render
 * @param state
 */

export const IconStatus = ({
  state,
  isSelected,
  hasFilter,
  isRendering,
}: {
  state: string;
  isSelected: boolean;
  hasFilter: boolean;
  isRendering: boolean;
}) => {
  let iconJsx;
  switch (state) {
    case "loading":
      iconJsx = (
        <div className={styles.LoadingStatus} style={{ display: "flex" }}>
          <span className={["spinner", "active", styles.Loader].join(" ")} />
        </div>
      );
      break;
    case "waiting":
      iconJsx = <StaticLoadingIcon />;
      break;
    default:
      if (isSelected) {
        iconJsx = checkBox(primary);
      } else {
        if (hasFilter || isRendering) {
          iconJsx = checkBox(teal_80);
        } else {
          iconJsx = checkBox(background2);
        }
      }
      break;
  }
  return iconJsx;
};
